
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getCurrentOrganisationId, supabase, isMockMode } from '../services/supabaseClient';
import { BrandingSettings, Organization } from '../types';

interface BrandingContextType {
  branding: BrandingSettings;
  organization: Organization | null;
  updateBranding: (settings: Partial<BrandingSettings>) => Promise<void>;
  loading: boolean;
}

const defaultBranding: BrandingSettings = {
  companyName: 'PCN Guard',
  logoUrl: '/logo.svg',
  // Keep navy as the default until the user/organization explicitly customizes branding.
  primaryColor: '#0F172A',
};

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

const BRANDING_STORAGE_KEY = 'pcn_fleet_branding_v1';
const LEGACY_AUTO_BRAND_COLOR = '#059669';

type PersistedBrandingPayload = {
  branding: BrandingSettings;
  organization: Organization | null;
  colorCustomized?: boolean;
};

const readPersistedBranding = (): PersistedBrandingPayload | null => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(BRANDING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.branding) return null;
    return {
      branding: parsed.branding as BrandingSettings,
      organization: (parsed.organization as Organization) ?? null,
      colorCustomized: Boolean(parsed.colorCustomized),
    };
  } catch {
    return null;
  }
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

// --- Color Utils ---
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const rgbToHex = (rgb: { r: number; g: number; b: number }) => {
  const toHex = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`.toUpperCase();
};

const normalizeHexColor = (value: string) => {
  const trimmed = value.trim();
  const match = /^#?([0-9a-f]{6})$/i.exec(trimmed);
  return match ? `#${match[1].toUpperCase()}` : null;
};

const srgbToLinear = (channel: number) => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

const relativeLuminance = (rgb: { r: number; g: number; b: number }) => {
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastRatio = (a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }) => {
  const L1 = relativeLuminance(a);
  const L2 = relativeLuminance(b);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
};

// Mix two colors: w is the weight of color1
const mix = (c1: {r:number, g:number, b:number}, c2: {r:number, g:number, b:number}, w: number) => {
  return {
    r: c1.r * w + c2.r * (1 - w),
    g: c1.g * w + c2.g * (1 - w),
    b: c1.b * w + c2.b * (1 - w)
  };
};

const ensureContrastForWhiteText = (base: { r: number; g: number; b: number }, minRatio = 4.5) => {
  const white = { r: 255, g: 255, b: 255 };
  if (contrastRatio(base, white) >= minRatio) return base;

  const black = { r: 0, g: 0, b: 0 };
  for (let i = 0; i < 40; i++) {
    const keep = 1 - (i + 1) / 40; // 0.975 → 0.0
    const candidate = mix(base, black, keep);
    if (contrastRatio(candidate, white) >= minRatio) return candidate;
  }

  return mix(base, black, 0.2);
};

const mixTowardWhiteForContrastOnDark = (
  base: { r: number; g: number; b: number },
  baseWeight: number,
  minRatio = 4.5,
  darkBg = { r: 2, g: 6, b: 23 } // slate-950 (#020617)
) => {
  const white = { r: 255, g: 255, b: 255 };
  if (contrastRatio(mix(base, white, baseWeight), darkBg) >= minRatio) return baseWeight;

  for (let i = 0; i < 30; i++) {
    const w = Math.max(0, baseWeight - (i + 1) * (baseWeight / 30));
    if (contrastRatio(mix(base, white, w), darkBg) >= minRatio) return w;
  }
  return 0;
};

const getAccessiblePrimaryColor = (value: string) => {
  const hex = normalizeHexColor(value);
  if (!hex) return null;
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const adjusted = ensureContrastForWhiteText(rgb, 4.5);
  return rgbToHex(adjusted);
};

const isLegacyAutoBrandColor = (value?: string | null) => {
  if (!value) return false;
  const normalized = normalizeHexColor(value);
  const legacy = normalizeHexColor(LEGACY_AUTO_BRAND_COLOR);
  return !!normalized && !!legacy && normalized === legacy;
};

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [branding, setBranding] = useState<BrandingSettings>(() => {
    const persisted = readPersistedBranding();
    if (!persisted?.branding) return defaultBranding;
    const accessiblePrimary = getAccessiblePrimaryColor(persisted.branding.primaryColor) || persisted.branding.primaryColor;
    const shouldUsePersistedColor = persisted.colorCustomized || !isLegacyAutoBrandColor(accessiblePrimary);
    return {
      ...persisted.branding,
      primaryColor: shouldUsePersistedColor ? accessiblePrimary : defaultBranding.primaryColor,
    };
  });
  const [organization, setOrganization] = useState<Organization | null>(() => readPersistedBranding()?.organization ?? null);
  const [colorCustomized, setColorCustomized] = useState<boolean>(() => readPersistedBranding()?.colorCustomized ?? false);
  const [loading, setLoading] = useState(true);
  const loadRequestIdRef = useRef(0);

  const normalizeLogoUrl = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (/^(https?:\/\/|data:|blob:)/i.test(trimmed)) return trimmed;
    if (trimmed.startsWith('/')) return trimmed;
    if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(trimmed)) return `https://${trimmed}`;
    return trimmed;
  };

  const clearPersisted = useCallback(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.removeItem(BRANDING_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const persist = useCallback((nextBranding: BrandingSettings, nextOrg: Organization | null, nextColorCustomized: boolean) => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(
        BRANDING_STORAGE_KEY,
        JSON.stringify({
          branding: nextBranding,
          organization: nextOrg,
          colorCustomized: nextColorCustomized,
          savedAt: new Date().toISOString(),
        })
      );
    } catch {
      // ignore
    }
  }, []);

  // Effect: Update Page Title and Favicon based on branding
  useEffect(() => {
    if (branding.companyName) {
      document.title = `${branding.companyName} | Fleet Dashboard`;
    }

    if (branding.logoUrl) {
      const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = branding.logoUrl;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [branding]);

  const applyTheme = useCallback((color: string) => {
    const accessibleHex = getAccessiblePrimaryColor(color);
    if (!accessibleHex) return;

    const base = hexToRgb(accessibleHex);
    if (!base) return;

    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };

    const w500 = mixTowardWhiteForContrastOnDark(base, 0.90);
    const w400 = mixTowardWhiteForContrastOnDark(base, 0.70);
    const w300 = mixTowardWhiteForContrastOnDark(base, 0.50);

    const palette = {
      50: mix(base, white, 0.05),
      100: mix(base, white, 0.15),
      200: mix(base, white, 0.30),
      300: mix(base, white, w300),
      400: mix(base, white, w400),
      500: mix(base, white, w500),
      600: base,
      700: mix(base, black, 0.90),
      800: mix(base, black, 0.75),
      900: mix(base, black, 0.60),
    };

    const root = document.documentElement;
    Object.entries(palette).forEach(([key, value]) => {
      root.style.setProperty(
        `--color-primary-${key}`,
        `${Math.round(value.r)} ${Math.round(value.g)} ${Math.round(value.b)}`
      );
    });
  }, []);

  // Keep CSS palette in sync with current brand color, and run before paint to prevent flicker.
  useLayoutEffect(() => {
    applyTheme(branding.primaryColor || defaultBranding.primaryColor);
  }, [applyTheme, branding.primaryColor]);

  const loadBranding = useCallback(async (providedSession?: Session | null) => {
    const requestId = ++loadRequestIdRef.current;
    try {
      setLoading(true);

      if (isMockMode) {
        // Mock data for demo environment
        const accessiblePrimary = getAccessiblePrimaryColor(defaultBranding.primaryColor) || defaultBranding.primaryColor;
        setBranding({
          companyName: 'Demo Fleet Ltd',
          logoUrl: '/logo.svg',
          primaryColor: accessiblePrimary,
        });
        setColorCustomized(false);
        setOrganization({
          id: 'mock-org-id',
          name: 'Demo Fleet Ltd',
          created_at: new Date().toISOString(),
          branding_color: accessiblePrimary,
          business_model: 'hire_firm_registered',
          legal_entity_verified: false,
          legal_entity_verification_status: 'unverified',
          enable_hire_workflows: true,
          enable_transfer_features: true
        });
        persist(
          { companyName: 'Demo Fleet Ltd', logoUrl: '/logo.svg', primaryColor: accessiblePrimary },
          {
            id: 'mock-org-id',
            name: 'Demo Fleet Ltd',
            created_at: new Date().toISOString(),
            branding_color: accessiblePrimary,
            business_model: 'hire_firm_registered',
            legal_entity_verified: false,
            legal_entity_verification_status: 'unverified',
            enable_hire_workflows: true,
            enable_transfer_features: true
          } as Organization,
          false
        );
        setLoading(false);
        return;
      }

      const session = providedSession ?? (await supabase.auth.getSession()).data.session;
      if (!session?.user) {
        if (requestId !== loadRequestIdRef.current) return;
        // Security boundary: never show previous tenant branding on signed-out screens.
        clearPersisted();
        setBranding(defaultBranding);
        setOrganization(null);
        setColorCustomized(false);
        setLoading(false);
        return;
      }

      // Source of truth: organisations table (works for multi-user orgs)
      const orgId = await getCurrentOrganisationId();
      const { data: org, error } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', orgId)
        .maybeSingle();

      if (error) throw error;
      if (!org) return;

      const accessibleOrgColor = org?.branding_color
        ? (getAccessiblePrimaryColor(org.branding_color) || org.branding_color)
        : null;
      const isOrgColorCustomized = !!accessibleOrgColor && !isLegacyAutoBrandColor(accessibleOrgColor);
      const effectiveUseCustomizedColor = colorCustomized || isOrgColorCustomized;

      const newBranding = {
        companyName: org?.name || defaultBranding.companyName,
        logoUrl: org?.logo_url || undefined,
        primaryColor: effectiveUseCustomizedColor
          ? (accessibleOrgColor || defaultBranding.primaryColor)
          : defaultBranding.primaryColor,
      };
      if (requestId !== loadRequestIdRef.current) return;
      setBranding(newBranding);
      setOrganization(org as Organization);
      setColorCustomized(effectiveUseCustomizedColor);
      persist(newBranding, org as Organization, effectiveUseCustomizedColor);
    } catch (e: any) {
      console.error(e);
    } finally {
      if (requestId === loadRequestIdRef.current) setLoading(false);
    }
  }, [clearPersisted, colorCustomized, persist]);

  useEffect(() => {
    void loadBranding();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        clearPersisted();
        setBranding(defaultBranding);
        setOrganization(null);
        setColorCustomized(false);
        return;
      }

      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        void loadBranding(session);
      }
    });

    return () => subscription.unsubscribe();
  }, [clearPersisted, loadBranding]);

  const updateBranding = async (settings: Partial<BrandingSettings>) => {
    try {
      if (isMockMode) {
        const normalizedLogoUrl =
          settings.logoUrl === undefined ? branding.logoUrl : normalizeLogoUrl(settings.logoUrl);
        const accessiblePrimary =
          settings.primaryColor ? (getAccessiblePrimaryColor(settings.primaryColor) || branding.primaryColor) : branding.primaryColor;
        const updatedState = { ...branding, ...settings, primaryColor: accessiblePrimary, logoUrl: normalizedLogoUrl };
        const updatedOrg =
          organization
            ? {
                ...organization,
                name: settings.companyName ? settings.companyName.trim() : organization.name,
                logo_url: settings.logoUrl !== undefined ? normalizeLogoUrl(settings.logoUrl) : organization.logo_url,
                branding_color: settings.primaryColor ? accessiblePrimary : organization.branding_color,
              }
            : organization;

        setBranding(updatedState);
        setOrganization(updatedOrg);
        const nextColorCustomized = colorCustomized || settings.primaryColor !== undefined;
        setColorCustomized(nextColorCustomized);
        persist(updatedState, updatedOrg, nextColorCustomized);
        return;
      }

      const orgId = await getCurrentOrganisationId();
      const updates: any = {};
      if (settings.companyName) updates.name = settings.companyName.trim();
      if (settings.logoUrl !== undefined) {
        updates.logo_url = normalizeLogoUrl(settings.logoUrl) ?? null;
      }
      const accessiblePrimary = settings.primaryColor ? getAccessiblePrimaryColor(settings.primaryColor) : null;
      if (settings.primaryColor && accessiblePrimary) updates.branding_color = accessiblePrimary;

      const { error } = await supabase
        .from('organisations')
        .update(updates)
        .eq('id', orgId);

      if (error) throw error;

      const normalizedLogoUrl =
        settings.logoUrl === undefined ? branding.logoUrl : normalizeLogoUrl(settings.logoUrl);
      const updatedState = {
        ...branding,
        ...settings,
        primaryColor: settings.primaryColor ? (accessiblePrimary ?? branding.primaryColor) : branding.primaryColor,
        logoUrl: normalizedLogoUrl
      };
      const updatedOrg =
        organization
          ? {
              ...organization,
              name: settings.companyName ? settings.companyName.trim() : organization.name,
              logo_url: settings.logoUrl !== undefined ? normalizeLogoUrl(settings.logoUrl) : organization.logo_url,
              branding_color: settings.primaryColor ? (accessiblePrimary ?? organization.branding_color) : organization.branding_color,
            }
          : organization;

      setBranding(updatedState);
      setOrganization(updatedOrg);
      const nextColorCustomized = colorCustomized || settings.primaryColor !== undefined;
      setColorCustomized(nextColorCustomized);
      persist(updatedState, updatedOrg, nextColorCustomized);
      
    } catch (err) {
      console.error('Failed to update branding', err);
      throw err;
    }
  };

  return (
    <BrandingContext.Provider value={{ branding, organization, updateBranding, loading }}>
      {children}
    </BrandingContext.Provider>
  );
};
